import { ResourceDaySlot } from './models/resourceDaySlot';
import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Association } from '@features/associations/models/association';
import { environment } from '@environments/environment';
import { AssociationUser } from './models/associationUser';
import { BookingPeriodAssociation } from './models/bookingPeriodAssociation';
import { CalendarDay } from '@shared/models/dateTypes';

// Endpoint root
const APIURL = environment.apiUrl;

/** Narrows `GET /associations/{id}/booking-periods` — see `bookingsPeriods`. */
export interface BookingPeriodsQuery {
  from?: CalendarDay;
  before?: CalendarDay;
  bookingId?: number;
}

@Injectable({
  providedIn: 'root',
})
export class AssociationsService {
  private http = inject(HttpClient);

  private currentUserAssociationIdSubject: BehaviorSubject<number>;
  public currentUserAssociationId: Observable<number>;
  public associations: Association[] = []; // list of all associations

  constructor() {
    // Get the current association from the session storage
    this.currentUserAssociationIdSubject = new BehaviorSubject<number>(
      JSON.parse(sessionStorage.getItem('currentUserAssociationId')),
    );
    this.currentUserAssociationId = this.currentUserAssociationIdSubject.asObservable();
  }

  setCurrentUserAssociation(associationId: number) {
    sessionStorage.setItem('currentUserAssociationId', JSON.stringify(associationId));
    this.currentUserAssociationIdSubject.next(associationId);
  }

  // API call
  update(payload: Association): Observable<Association> {
    return this.http.post<Association>(`${APIURL}/associations/`, payload).pipe(
      map((data) => {
        this.setCurrentUserAssociation(data.id);
        this.currentUserAssociationIdSubject.next(data.id);
        return data;
      }),
    );
  }
  // API call
  getList(): Observable<Association[]> {
    return this.http.get<Association[]>(`${APIURL}/associations`).pipe(
      tap((data: Association[]) => {
        return data;
      }),
    );
  }
  // API call
  get(id: number): Observable<Association> {
    return this.http.get<Association>(`${APIURL}/associations/${id}`);
  }

  /** The day calendar behind one of the association's periods: everything on its resource that day. */
  resourceDay(associationId: number | string, bookingPeriodId: number): Observable<ResourceDaySlot[]> {
    return this.http.get<ResourceDaySlot[]>(
      `${APIURL}/associations/${associationId}/booking-periods/${bookingPeriodId}/resource-day`,
    );
  }

  getUsers(userId: number): Observable<AssociationUser[]> {
    return this.http.get<AssociationUser[]>(`${APIURL}/associations/users?userId=${userId}`);
  }

  /**
   * The association's periods. Without `query` all of them; `from` gives the bookings with a period
   * ending that day or later (each booking whole), `before` the same date the rest; `bookingId` only
   * that booking's periods.
   */
  bookingsPeriods(associationId: number, query: BookingPeriodsQuery = {}): Observable<BookingPeriodAssociation[]> {
    const params: Record<string, string> = {};
    if (query.from) params['from'] = query.from;
    if (query.before) params['before'] = query.before;
    if (query.bookingId != null) params['booking_id'] = String(query.bookingId);
    return this.http.get<BookingPeriodAssociation[]>(`${APIURL}/associations/${associationId}/booking-periods`, {
      params,
    });
  }
}
